import React, { useState, useEffect } from "react";
import "./ProgressBar.css";
import Tooltip from "../tooltip/Tooltip";
import { Box, SxProps, Theme } from "@mui/material";
import { styled } from '@mui/material/styles';
import { formatNumber, formatPercentage } from "../../utils/formatNumber";

const Svg = styled('svg')({
  aspectRatio: '1 / 1',
  display: 'block',
  height: '100%',
  transform: 'rotate(-90deg)',
  width: '100%',
});

// TODO get rid of this legend, use the common one
const LegendContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: theme.spacing(0.5, 1),
  fontSize: theme.typography.caption.fontSize,
  marginTop: theme.spacing(0.5),
  textAlign: 'center',
}));

const ProgressContainer = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  flexFlow: 'column',
  justifyContent: 'center',
  width: '100%',
});

interface ProgressBar {
  current: number;
  max: number;
  color: string;
}

interface SpeedometerProps {
  progressBars: ProgressBar[];
  titleBars: string[];
  showPercentage: boolean;
  defaultPercentageValue?: number;
  showLegend?: boolean;
  sx?: SxProps<Theme>;
  width?: number;
  height?: number;
}

const ProgressBarComponent: React.FC<SpeedometerProps> = ({
  progressBars,
  titleBars,
  showPercentage,
  defaultPercentageValue,
  showLegend = true,
  sx,
  width,
  height,
}) => {
  const [tooltip, setTooltip] = useState({
    items: [] as { text: string; color: string }[],
    position: { x: 0, y: 0 },
    visible: false,
  });

  const maxRadius = 60;
  const radiusStep = 10;

  const handleMouseEnter = (event: React.MouseEvent | React.TouchEvent) => {
    const items = progressBars.map((progress, index) => ({
      text: `${titleBars[index]}: ${formatNumber(progress.current)}`,
      color: progress.color,
    }));

    let x = 0;
    let y = 0;

    if ("touches" in event) {
      x = event.touches[0].clientX;
      y = event.touches[0].clientY;
    } else if ("clientX" in event) {
      x = event.clientX;
      y = event.clientY;
    }

    setTooltip({
      items,
      position: { x: x + 20, y: y },
      visible: true,
    });
  };
  const handleMouseMove = (event: React.MouseEvent | React.TouchEvent) => {
    let x = 0;
    let y = 0;

    if ("touches" in event) {
      x = event.touches[0].clientX;
      y = event.touches[0].clientY;
    } else if ("clientX" in event) {
      x = event.clientX;
      y = event.clientY;
    }

    setTooltip((prev) => ({
      ...prev,
      position: { x: x + 20, y: y },
    }));
  };
  const handleMouseLeave = () => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  useEffect(() => {
    const handleResize = () => {
      setTooltip((prev) => ({ ...prev, visible: false }));
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const percentageValue = defaultPercentageValue ??
    Math.round(
      ((progressBars[0].current - progressBars[1].current) /
        progressBars[1].current) *
      100
    );

  return (
    <ProgressContainer
      sx={sx}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleMouseEnter}
      onTouchMove={handleMouseMove}
      onTouchEnd={handleMouseLeave}
    >
      {/* todo get rid of classname */}
      <Svg className="progress-bar" viewBox="0 0 130 130" {...(width ? { width } : {})} {...(height ? { height } : {})}>
        {progressBars.map((progress, index) => {
          const radius = maxRadius - index * radiusStep;
          const circumference = 2 * Math.PI * radius;
          const normalizedProgress = Math.min(
            Math.max(progress.current, 0),
            progress.max
          );
          const progressOffset =
            circumference * (1 - normalizedProgress / progress.max);
          const color = progress.color;

          return (
            <circle
              key={index}
              className="speedometer-progress"
              cx="65"
              cy="65"
              r={radius}
              strokeWidth="9"
              stroke={color}
              fill="none"
              strokeDasharray={`${circumference} ${circumference}`}
              strokeDashoffset={progressOffset}
              style={{
                transition: "stroke-dashoffset 0.35s",
                transformOrigin: "center",
              }}
            />
          );
        })}
        {showPercentage ? (
          <>
            <text
              transform={`rotate(90 65 65)`}
              x="50%"
              y="40%"
              textAnchor="middle"
              dominantBaseline="middle"
              fill={progressBars[0].color}
              fontSize='1.2rem'
            >
              {formatNumber(progressBars[0].current)}
            </text>
            <text
              transform="rotate(90 65 65)"
              x="50%"
              y="58%"
              textAnchor="middle"
              dominantBaseline="middle"
              fill={progressBars[1].color}
              fontSize='1.2rem'
            >
              {progressBars[1].current !== 0 ? formatPercentage(percentageValue, true) : ""}
            </text>
          </>
        ) : (
          <>
            <text
              transform="rotate(90 65 65)"
              x="50%"
              y="50%"
              textAnchor="middle"
              dominantBaseline="middle"
              fill={progressBars[0].color}
              fontSize='1.4rem'
            >
              {progressBars[0].current}
            </text>
          </>
        )}
      </Svg>
      {showLegend && (
        <LegendContainer>
          {titleBars.map((title, index) => (
            <div key={index} style={{ color: progressBars[index].color }}>
              {title}
            </div>
          ))}
        </LegendContainer>
      )}
      {progressBars.length > 2 ? (
        <Tooltip
          items={tooltip.items}
          position={tooltip.position}
          visible={tooltip.visible}
        />
      ) : null}
    </ProgressContainer>
  );
};

export default ProgressBarComponent;
